import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Project } from '../types';
import { ProjectService } from '../lib/projectService';
import {
  ProjectMemberService,
  ProjectMember,
  ProjectWithOwner,
  ProjectRole
} from '../lib/projectMemberService';

interface ProjectContextType {
  // Existing
  selectedProjectId: string | null;
  setSelectedProjectId: (projectId: string | null) => void;
  projects: Project[];
  setProjects: React.Dispatch<React.SetStateAction<Project[]>>;
  refreshProjects: () => Promise<void>;
  projectsLoading: boolean;

  // New: Joined projects (projects the user is a member of, not owner)
  joinedProjects: ProjectWithOwner[];
  refreshJoinedProjects: () => Promise<void>;
  joinedProjectsLoading: boolean;

  // New: Current user's role in selected project
  currentUserRole: ProjectRole;
  refreshCurrentRole: () => Promise<void>;

  // New: Members of the selected project
  projectMembers: ProjectMember[];
  refreshProjectMembers: () => Promise<void>;
  membersLoading: boolean;

  // New: Member management actions
  inviteMember: (username: string, role: 'viewer' | 'editor') => Promise<{ success: boolean; error?: string }>;
  removeMember: (membershipId: string) => Promise<boolean>;
  updateMemberRole: (membershipId: string, role: 'viewer' | 'editor') => Promise<boolean>;
  leaveProject: () => Promise<boolean>;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

const PROJECT_STORAGE_KEY = 'ezart_selected_project';

export const ProjectProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedProjectId, setSelectedProjectIdState] = useState<string | null>(() => {
    // Initialize from localStorage
    if (typeof window !== 'undefined') {
      return localStorage.getItem(PROJECT_STORAGE_KEY);
    }
    return null;
  });

  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);

  // New state for joined projects
  const [joinedProjects, setJoinedProjects] = useState<ProjectWithOwner[]>([]);
  const [joinedProjectsLoading, setJoinedProjectsLoading] = useState(true);

  // New state for current user's role
  const [currentUserRole, setCurrentUserRole] = useState<ProjectRole>(null);

  // New state for project members
  const [projectMembers, setProjectMembers] = useState<ProjectMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);

  const setSelectedProjectId = (projectId: string | null) => {
    setSelectedProjectIdState(projectId);

    // Sync to localStorage
    if (typeof window !== 'undefined') {
      if (projectId) {
        localStorage.setItem(PROJECT_STORAGE_KEY, projectId);
      } else {
        localStorage.removeItem(PROJECT_STORAGE_KEY);
      }
    }
  };

  const refreshProjects = useCallback(async () => {
    setProjectsLoading(true);
    try {
      const data = await ProjectService.getProjects();
      setProjects(data);
    } catch (error) {
      console.error('Failed to refresh projects:', error);
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  // New: Refresh joined projects
  const refreshJoinedProjects = useCallback(async () => {
    setJoinedProjectsLoading(true);
    try {
      const data = await ProjectMemberService.getJoinedProjects();
      setJoinedProjects(data);
    } catch (error) {
      console.error('Failed to refresh joined projects:', error);
    } finally {
      setJoinedProjectsLoading(false);
    }
  }, []);

  // New: Refresh current user's role in selected project
  const refreshCurrentRole = useCallback(async () => {
    if (!selectedProjectId) {
      setCurrentUserRole(null);
      return;
    }
    try {
      const result = await ProjectMemberService.checkProjectAccess(selectedProjectId);
      setCurrentUserRole(result.role);
    } catch (error) {
      console.error('Failed to check project access:', error);
      setCurrentUserRole(null);
    }
  }, [selectedProjectId]);

  // New: Refresh project members
  const refreshProjectMembers = useCallback(async () => {
    if (!selectedProjectId) {
      setProjectMembers([]);
      return;
    }
    setMembersLoading(true);
    try {
      const members = await ProjectMemberService.getProjectMembers(selectedProjectId);
      setProjectMembers(members);
    } catch (error) {
      console.error('Failed to refresh project members:', error);
    } finally {
      setMembersLoading(false);
    }
  }, [selectedProjectId]);

  // New: Invite a member
  const inviteMember = useCallback(async (
    username: string,
    role: 'viewer' | 'editor'
  ): Promise<{ success: boolean; error?: string }> => {
    if (!selectedProjectId) {
      return { success: false, error: 'No project selected' };
    }
    const result = await ProjectMemberService.inviteMember(selectedProjectId, username, role);
    if (result.success) {
      await refreshProjectMembers();
    }
    return result;
  }, [selectedProjectId, refreshProjectMembers]);

  // New: Remove a member
  const removeMember = useCallback(async (membershipId: string): Promise<boolean> => {
    const success = await ProjectMemberService.removeMember(membershipId);
    if (success) {
      await refreshProjectMembers();
    }
    return success;
  }, [refreshProjectMembers]);

  // New: Update member role
  const updateMemberRole = useCallback(async (
    membershipId: string,
    role: 'viewer' | 'editor'
  ): Promise<boolean> => {
    const success = await ProjectMemberService.updateMemberRole(membershipId, role);
    if (success) {
      await refreshProjectMembers();
    }
    return success;
  }, [refreshProjectMembers]);

  // New: Leave project
  const leaveProject = useCallback(async (): Promise<boolean> => {
    if (!selectedProjectId) return false;
    const success = await ProjectMemberService.leaveProject(selectedProjectId);
    if (success) {
      // Clear selection and refresh
      setSelectedProjectId(null);
      await refreshJoinedProjects();
    }
    return success;
  }, [selectedProjectId, refreshJoinedProjects]);

  // Load projects on mount
  useEffect(() => {
    refreshProjects();
    refreshJoinedProjects();
  }, [refreshProjects, refreshJoinedProjects]);

  // Refresh role and members when selected project changes
  useEffect(() => {
    refreshCurrentRole();
    refreshProjectMembers();
  }, [selectedProjectId, refreshCurrentRole, refreshProjectMembers]);

  return (
    <ProjectContext.Provider value={{
      // Existing
      selectedProjectId,
      setSelectedProjectId,
      projects,
      setProjects,
      refreshProjects,
      projectsLoading,
      // New
      joinedProjects,
      refreshJoinedProjects,
      joinedProjectsLoading,
      currentUserRole,
      refreshCurrentRole,
      projectMembers,
      refreshProjectMembers,
      membersLoading,
      inviteMember,
      removeMember,
      updateMemberRole,
      leaveProject
    }}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};
